import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Plus, Search, Edit2, X, ChevronDown, ChevronUp, Building, Trash2, ArrowRight } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';

const emptyVendor = {
  name: '', contact_person: '', email: '', phone: '', fax: '',
  address: '', city: '', state: '', zip_code: '', country: '',
  remittance_address: '', remittance_city: '', remittance_state: '', remittance_zip: '', remittance_country: '',
  shipping_from_address: '',
  website: '', payment_terms: '', tax_scheme: 'sales_tax', notes: ''
};

const VENDOR_FORM_KEY = 'webb_vendor_form';

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const vendSaved = loadDraft(VENDOR_FORM_KEY);
  const [showForm, setShowForm] = useState(vendSaved?.showForm || false);
  const [editingId, setEditingId] = useState(vendSaved?.editingId || null);
  const [form, setForm] = useState(vendSaved?.form || { ...emptyVendor });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Move from customers state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedToMove, setSelectedToMove] = useState(new Set());
  const [moving, setMoving] = useState(false);
  const [custSearch, setCustSearch] = useState('');

  const [vendorProducts, setVendorProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const toggleExpand = async (vendorId) => {
    if (expandedId === vendorId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(vendorId);
    setLoadingProducts(true);
    try {
      const data = await api.request(`/api/vendors/${vendorId}/products`);
      setVendorProducts(data);
    } catch { setVendorProducts([]); }
    finally { setLoadingProducts(false); }
  };

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const data = await api.getVendors({ search: search || undefined });
      setVendors(data);
    } catch (err) { console.error('Failed to load vendors', err); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchVendors is stable; only re-run on search change
  useEffect(() => { fetchVendors(); }, [search]);

  // Autosave vendor form
  useAutosave(VENDOR_FORM_KEY, { form, showForm, editingId });

  const sorted = [...vendors].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="inline h-3 w-3 ml-0.5" /> : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const handleEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name || '', contact_person: v.contact_person || '',
      email: v.email || '', phone: v.phone || '', fax: v.fax || '',
      address: v.address || '', city: v.city || '',
      state: v.state || '', zip_code: v.zip_code || '',
      country: v.country || '', website: v.website || '',
      remittance_address: v.remittance_address || '', remittance_city: v.remittance_city || '',
      remittance_state: v.remittance_state || '', remittance_zip: v.remittance_zip || '',
      remittance_country: v.remittance_country || '',
      shipping_from_address: v.shipping_from_address || '',
      payment_terms: v.payment_terms || '', notes: v.notes || '',
      tax_scheme: v.tax_scheme || 'sales_tax',
    });
    setShowForm(true);
  };

  const handleNew = () => { setEditingId(null); setForm({ ...emptyVendor }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Vendor name is required'); return; }
    if (!form.address.trim()) { alert('Address is required'); return; }
    if (!form.phone.trim()) { alert('Phone number is required'); return; }
    if (!form.remittance_address.trim()) { alert('Remittance address is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.updateVendor(editingId, form);
      } else {
        await api.createVendor(form);
      }
      setShowForm(false);
      setEditingId(null);
      clearDraft(VENDOR_FORM_KEY);
      fetchVendors();
    } catch (err) { alert('Failed to save: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await api.deleteVendor(id);
      fetchVendors();
    } catch (err) { alert('Failed to delete: ' + err.message); }
  };

  const openMoveModal = async () => {
    try {
      const data = await api.getCustomers();
      setCustomers(Array.isArray(data) ? data : []);
      setSelectedToMove(new Set());
      setCustSearch('');
      setShowMoveModal(true);
    } catch (err) { alert('Failed to load customers: ' + err.message); }
  };

  const toggleSelect = (id) => {
    setSelectedToMove(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const filtered = customers.filter(c => {
      if (!custSearch) return true;
      return (c.name || '').toLowerCase().includes(custSearch.toLowerCase());
    });
    if (selectedToMove.size === filtered.length) {
      setSelectedToMove(new Set());
    } else {
      setSelectedToMove(new Set(filtered.map(c => c.id)));
    }
  };

  const handleMoveToVendors = async () => {
    if (selectedToMove.size === 0) return;
    if (!window.confirm(`Move ${selectedToMove.size} entries from Customers to Vendors?`)) return;
    setMoving(true);
    try {
      const res = await api.request('/api/vendors/move-from-customers', {
        method: 'POST',
        body: JSON.stringify({ customer_ids: Array.from(selectedToMove) }),
      });
      alert(`Moved ${res.moved} entries to Vendors`);
      setShowMoveModal(false);
      fetchVendors();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setMoving(false); }
  };

  const filteredCustomers = customers.filter(c => {
    if (!custSearch) return true;
    return (c.name || '').toLowerCase().includes(custSearch.toLowerCase()) ||
           (c.email || '').toLowerCase().includes(custSearch.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Vendors</h2>
          <p className="text-muted-foreground">{sorted.length} vendors</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openMoveModal} className="flex items-center gap-2 px-3 py-2 border border-input rounded-md hover:bg-muted text-sm" data-testid="move-from-customers-btn">
            <ArrowRight className="h-4 w-4" /> Move from Customers
          </button>
          <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="new-vendor-btn">
            <Plus className="h-4 w-4" /> New Vendor
          </button>
        </div>
      </div>

      {/* Move from Customers Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Move Entries to Vendors</h3>
                <p className="text-sm text-muted-foreground">Select customer entries that are actually vendors</p>
              </div>
              <button onClick={() => setShowMoveModal(false)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 border-b">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input type="text" placeholder="Search..." value={custSearch} onChange={e => setCustSearch(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-input rounded text-sm" />
                <button type="button" onClick={selectAll} className="text-xs text-primary hover:underline">
                  {selectedToMove.size === filteredCustomers.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredCustomers.map(c => (
                <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 rounded cursor-pointer">
                  <input type="checkbox" checked={selectedToMove.has(c.id)} onChange={() => toggleSelect(c.id)}
                    className="rounded border-border h-4 w-4" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[c.email, c.phone, c.city].filter(Boolean).join(' | ') || 'No details'}
                    </div>
                  </div>
                </label>
              ))}
              {filteredCustomers.length === 0 && (
                <p className="text-center text-muted-foreground py-8 text-sm">No customers found</p>
              )}
            </div>
            <div className="p-4 border-t flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{selectedToMove.size} selected</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowMoveModal(false)} className="px-4 py-2 border rounded-md hover:bg-muted text-sm">Cancel</button>
                <button type="button" onClick={handleMoveToVendors} disabled={selectedToMove.size === 0 || moving}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 text-sm font-medium">
                  {moving ? 'Moving...' : `Move ${selectedToMove.size} to Vendors`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search vendors..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-input rounded-md" data-testid="vendor-search" />
      </div>

      {/* Vendor Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="vendor-form-modal">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit Vendor' : 'New Vendor'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Vendor / Company Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contact Person</label>
                <input type="text" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-contact" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fax #</label>
                <input type="text" value={form.fax} onChange={(e) => setForm({...form, fax: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-fax" />
              </div>

              {/* Address (Required) */}
              <div className="col-span-2 pt-2 border-t">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Address *</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Street Address *</label>
                <input type="text" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-address" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input type="text" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-city" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input type="text" value={form.state} onChange={(e) => setForm({...form, state: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-state" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP Code</label>
                <input type="text" value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-zip" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <input type="text" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-country" />
              </div>

              {/* Remittance Address (Required) */}
              <div className="col-span-2 pt-2 border-t">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Remittance Address *</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Remittance Street Address *</label>
                <input type="text" value={form.remittance_address} onChange={(e) => setForm({...form, remittance_address: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-remittance-address" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input type="text" value={form.remittance_city} onChange={(e) => setForm({...form, remittance_city: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-remittance-city" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State</label>
                <input type="text" value={form.remittance_state} onChange={(e) => setForm({...form, remittance_state: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-remittance-state" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ZIP</label>
                <input type="text" value={form.remittance_zip} onChange={(e) => setForm({...form, remittance_zip: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-remittance-zip" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <input type="text" value={form.remittance_country} onChange={(e) => setForm({...form, remittance_country: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-remittance-country" />
              </div>

              {/* Shipping From Address (Optional) */}
              <div className="col-span-2 pt-2 border-t">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Shipping From Address (Optional)</p>
              </div>
              <div className="col-span-2">
                <input type="text" value={form.shipping_from_address} onChange={(e) => setForm({...form, shipping_from_address: e.target.value})}
                  placeholder="Shipping origin address..." className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-shipping-from" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input type="text" value={form.website} onChange={(e) => setForm({...form, website: e.target.value})}
                  placeholder="https://" className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-website" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Terms</label>
                <select value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-terms">
                  <option value="">Select...</option>
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 60">Net 60</option>
                  <option value="Net 90">Net 90</option>
                  <option value="Due on Receipt">Due on Receipt</option>
                  <option value="COD">COD</option>
                  <option value="Prepaid">Prepaid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tax Scheme</label>
                <select value={form.tax_scheme} onChange={(e) => setForm({...form, tax_scheme: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="vendor-form-tax-scheme">
                  <option value="sales_tax">Sales Tax</option>
                  <option value="no_tax">No Tax</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md h-20" data-testid="vendor-form-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-input rounded-md hover:bg-muted">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="save-vendor-btn">
                {saving ? 'Saving...' : (editingId ? 'Update Vendor' : 'Create Vendor')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-8 px-2"></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  Vendor <SortIcon field="name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Terms</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Tax Scheme</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                  <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />No vendors found
                </td></tr>
              ) : (
                sorted.map((v) => (
                  <React.Fragment key={v.id}>
                    <tr className="hover:bg-muted/30 cursor-pointer" onClick={() => toggleExpand(v.id)} data-testid={`vendor-row-${v.id}`}>
                      <td className="px-2 py-3 text-center">
                        {expandedId === v.id ? <ChevronUp className="h-4 w-4 text-muted-foreground inline" /> : <ChevronDown className="h-4 w-4 text-muted-foreground inline" />}
                      </td>
                      <td className="px-4 py-3 font-medium">{v.name}</td>
                      <td className="px-4 py-3 text-sm">{v.contact_person || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">{v.phone || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">{v.email || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">{v.payment_terms || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.tax_scheme === 'no_tax' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                          {v.tax_scheme === 'no_tax' ? 'No Tax' : 'Sales Tax'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(v); }} className="p-1.5 hover:bg-muted rounded-md" data-testid={`edit-vendor-${v.id}`}>
                            <Edit2 className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(v.id); }} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-vendor-${v.id}`}>
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === v.id && (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 bg-muted/20">
                          <div className="grid grid-cols-3 gap-6">
                            <div className="space-y-2 text-sm">
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Address</h4>
                              {v.address && <div>{v.address}</div>}
                              <div>{[v.city, v.state, v.zip_code].filter(Boolean).join(', ')}</div>
                              {v.country && <div>{v.country}</div>}
                            </div>
                            <div className="space-y-2 text-sm">
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Details</h4>
                              {v.website && <div><span className="text-muted-foreground">Website:</span> {v.website}</div>}
                              {v.payment_terms && <div><span className="text-muted-foreground">Terms:</span> {v.payment_terms}</div>}
                            </div>
                            <div className="space-y-2 text-sm">
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Notes</h4>
                              <div>{v.notes || 'No notes'}</div>
                            </div>
                          </div>

                          {/* Products Purchased from this Vendor */}
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Products Purchased ({vendorProducts.length})</h4>
                            {loadingProducts ? (
                              <p className="text-sm text-muted-foreground">Loading...</p>
                            ) : vendorProducts.length === 0 ? (
                              <p className="text-sm text-muted-foreground">No products found from this vendor</p>
                            ) : (
                              <table className="w-full text-sm">
                                <thead><tr className="border-b">
                                  <th className="text-left py-1.5 text-xs font-medium text-muted-foreground">Product</th>
                                  <th className="text-left py-1.5 text-xs font-medium text-muted-foreground">SKU</th>
                                  <th className="text-left py-1.5 text-xs font-medium text-muted-foreground">Last Ordered</th>
                                </tr></thead>
                                <tbody className="divide-y">
                                  {vendorProducts.map((p, i) => (
                                    <tr key={i} className="hover:bg-muted/30">
                                      <td className="py-1.5">
                                        <Link to={`/products/${p.product_id}`} className="text-primary hover:underline font-medium" onClick={e => e.stopPropagation()}>
                                          {p.product_name} {p.variant_name !== p.product_name && p.variant_name ? `- ${p.variant_name}` : ''}
                                        </Link>
                                      </td>
                                      <td className="py-1.5 text-muted-foreground">{p.sku}</td>
                                      <td className="py-1.5 text-muted-foreground">{p.last_ordered ? new Date(p.last_ordered).toLocaleDateString() : ''}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Vendors;
